import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';

import { environment } from 'src/environments/environment';

// Module endpoints (/users, /access, /auth) live at the domain root on the
// v2 back end; only the fleet API sits under /api. Same origin either way.
const API_ROOT = environment.apiUrl.replace(/\/api$/, '');

@Injectable({ providedIn: 'root' })
export class AdminService {
  private myAccess$?: Observable<{ roles: any[]; permissions: string[] }>;

  constructor(private http: HttpClient) {}

  /** The caller's own roles/permissions — cached per session for menu gating. */
  myAccess(): Observable<{ roles: any[]; permissions: string[] }> {
    if (!this.myAccess$) {
      this.myAccess$ = this.http
        .get<{ roles: any[]; permissions: string[] }>(`${API_ROOT}/access/me`)
        .pipe(shareReplay(1));
    }
    return this.myAccess$;
  }

  /** Drop the cached access (on logout/login switch). */
  resetAccessCache() {
    this.myAccess$ = undefined;
  }

  isAdmin(): Observable<boolean> {
    return this.myAccess().pipe(
      map((access) => access.permissions.includes('user.manage')),
      catchError(() => of(false))
    );
  }

  /** The signed-in user's own profile — name/email for the sidebar. */
  profile(): Observable<any> {
    return this.http.get<any>(`${environment.apiUrl}/users/me`);
  }

  // --- users ---------------------------------------------------------
  listUsers() {
    return this.http.get<any[]>(`${API_ROOT}/users`);
  }

  /** send_invite: the new user gets a set-your-password email; no password
   *  ever travels through the admin's browser. */
  createUser(email: string, fullName: string | null) {
    return this.http.post<any>(`${API_ROOT}/users`, {
      email,
      full_name: fullName,
      send_invite: true,
    });
  }

  setUserActive(userId: number, active: boolean) {
    const action = active ? 'activate' : 'deactivate';
    return this.http.post<any>(`${API_ROOT}/users/${userId}/${action}`, {});
  }

  updateUser(userId: number, changes: { email?: string; full_name?: string }) {
    return this.http.patch<any>(`${API_ROOT}/users/${userId}`, changes);
  }

  userRoles(userId: number) {
    return this.http.get<any>(`${API_ROOT}/users/${userId}/roles`);
  }

  listRoles() {
    return this.http.get<any[]>(`${API_ROOT}/access/roles`);
  }

  assignRole(userId: number, roleId: number) {
    return this.http.post(`${API_ROOT}/users/${userId}/roles/${roleId}`, {});
  }

  removeRole(userId: number, roleId: number) {
    return this.http.delete(`${API_ROOT}/users/${userId}/roles/${roleId}`);
  }

  userSessions(userId: number) {
    return this.http.get<any[]>(`${API_ROOT}/users/${userId}/sessions`);
  }

  revokeSession(userId: number, sessionId: number) {
    return this.http.delete(`${API_ROOT}/users/${userId}/sessions/${sessionId}`);
  }

  // --- audit ---------------------------------------------------------
  loginActivity(skip: number, limit: number) {
    const params = new HttpParams().set('skip', skip).set('limit', limit);
    return this.http.get<{ total_records: number; records: any[] }>(
      `${environment.apiUrl}/admin/login-activity`,
      { params }
    );
  }

  // --- report schedule ----------------------------------------------
  getReportSchedule() {
    return this.http.get<any>(`${environment.apiUrl}/report-schedule`);
  }

  updateReportSchedule(schedule: any) {
    return this.http.put<any>(`${environment.apiUrl}/report-schedule`, schedule);
  }

  // --- audit trail (module endpoint at the domain root) --------------
  auditLog(page: number, pageSize: number) {
    const params = new HttpParams().set('page', page).set('page_size', pageSize);
    return this.http.get<{ items: any[]; total: number }>(`${API_ROOT}/audit`, {
      params,
    });
  }

  // --- email / reset log --------------------------------------------
  emailLog(skip: number, limit: number) {
    const params = new HttpParams().set('skip', skip).set('limit', limit);
    return this.http.get<{ total_records: number; records: any[] }>(
      `${environment.apiUrl}/admin/email-log`,
      { params }
    );
  }

  // --- password actions ---------------------------------------------
  sendReset(userId: number) {
    return this.http.post<any>(
      `${environment.apiUrl}/admin/users/${userId}/send-reset`,
      {}
    );
  }

  setPassword(userId: number, password: string) {
    return this.http.post<any>(`${API_ROOT}/users/${userId}/password`, {
      password,
    });
  }

  // --- predictive maintenance (fleet API, under /api) ----------------
  /** Cached per-vehicle risk, highest first. min_level: 'high' | 'medium'. */
  getPredictions(minLevel?: 'high' | 'medium') {
    let params = new HttpParams();
    if (minLevel) params = params.set('min_level', minLevel);
    return this.http.get<any[]>(`${environment.apiUrl}/maintenance/predictions`, {
      params,
    });
  }

  /** Rebuild predictions now (rule-based + AI narrative if a provider is set). */
  recomputePredictions() {
    return this.http.post<{ message: string }>(
      `${environment.apiUrl}/maintenance/recompute`,
      {}
    );
  }

  // --- AI provider config (module endpoint at the domain root) -------
  listProviders() {
    return this.http.get<any[]>(`${API_ROOT}/ai/providers`);
  }

  createProvider(body: {
    provider: string;
    model_name: string;
    api_key?: string;
    is_default?: boolean;
  }) {
    return this.http.post<any>(`${API_ROOT}/ai/providers`, body);
  }

  updateProvider(
    id: number,
    body: {
      model_name?: string;
      api_key?: string;
      is_active?: boolean;
      is_default?: boolean;
    }
  ) {
    return this.http.patch<any>(`${API_ROOT}/ai/providers/${id}`, body);
  }

  testProvider(id: number) {
    return this.http.post<{ ok: boolean; message: string }>(
      `${API_ROOT}/ai/providers/${id}/test`,
      {}
    );
  }

  deleteProvider(id: number) {
    return this.http.delete(`${API_ROOT}/ai/providers/${id}`);
  }
}
